import React from "react";
import cns from "classnames";
import { withStyles } from "@material-ui/core";
import CssBaseline from "@material-ui/core/CssBaseline";
import { ThemeType } from "@theme";
import { useSidebarLock } from "../../context/sidebar-lock-context";

import { reactSelectGlobalMuiStyles } from "../Select/utils";

type Props = {
  classes: any;
  sideBar: React.ReactNode;
  content: React.ReactNode;
  forceSidebarStay: boolean;
};

const SiteLayout: React.FC<Props> = ({ classes, ...props }) => {
  const { locked } = useSidebarLock();
  const sideBarClassName = cns(classes.sidebar, {
    forceStay: props.forceSidebarStay || locked
  });

  return (
    <div className={classes.root} data-test="SiteLayout">
      <CssBaseline />

      <div className={classes.main}>
        <div className={sideBarClassName} data-test="SideBar">
          {props.sideBar}
        </div>

        <div className={classes.content}>{props.content}</div>
      </div>
    </div>
  );
};

const expandedOverrides = (theme: ThemeType) => ({
  width: [theme.sidebar.defaultWidth, "!important"],
  "& .sidebar-logo-full": {
    display: "inline"
  },
  "& .sidebar-logo-collapsed": {
    display: "none"
  },
  "& .sidebar-hide-collapsed": {
    color: theme.palette.text.secondary
  },
  "& .sidebar-no-indent-collapsed": {
    paddingLeft: "32px !important"
  }
});

const styles = (theme: ThemeType) => ({
  "@global": {
    html: {
      height: "100%"
    },

    body: {
      height: "100%",
      // minHeight: "100vh",
      // height: "100vh",
      // We need it because of mui's colorInherit class sometimes canceling default typography color
      color: theme.bodyMainColor,
      overflow: "hidden"
    },

    "#root": {
      height: "100%",
      minHeight: "100vh",
      width: "inherit"
    },

    ".rc-slider-mark-text": {
      fontFamily: "Roboto"
    },
    ".leaflet-container": {
      width: "100%",
      height: "90vh"
    },

    ".leaflet-popup-content": {
      margin: 0
    },

    ".leaflet-popup-content-wrapper ": {
      borderRadius: 0
    },

    ".snackbar-snack": {
      zIndex: 2000
    },

    ...reactSelectGlobalMuiStyles(theme)
  },

  root: {
    display: "flex",
    height: "100%",
    flexDirection: "column",
    width: "inherit",
    minHeight: "100vh",
    marginBottom: -4
  },

  appbar: {
    flex: "0 0 0",
    zIndex: 3
  },

  main: {
    alignItems: "stretch",
    flex: "1 0 100%",
    display: "flex"
  },

  sidebar: {
    backgroundColor: theme.appBar.background,
    boxShadow: "none",
    borderRight: `1px solid ${theme.palette.divider}`,
    display: "flex",
    flex: "0 0 auto",
    flexDirection: "column",
    height: "100%",
    overflowX: "hidden",
    transition: theme.transitions.create("width"),
    width: theme.sidebar.narrowWidth,
    zIndex: 2,
    position: "fixed",
    "z-index": 101,

    "& .sidebar-logo-full": {
      display: "none"
    },
    "& .sidebar-logo-collapsed": {
      display: "block"
    },
    "& .sidebar-hide-collapsed": {
      color: "transparent"
    },
    "& .sidebar-no-indent-collapsed": {
      paddingLeft: `${theme.spacing(2)}px !important`
    },

    "&.forceStay, &:hover": expandedOverrides(theme),

    [theme.breakpoints.up(theme.breakpoints.values.xl - 1)]: expandedOverrides(theme),

    "& > *": {
      height: "100vh" // hack for fixing outer div in material's Paper component
    }
  },

  content: {
    display: "flex",
    flexDirection: "column",
    flex: "1 0 0",
    height: "100%", // this is very important, don't change it
    marginLeft: theme.sidebar.narrowWidth,
    maxWidth: `calc(100% - ${theme.sidebar.narrowWidth}px)`,
    transition: theme.transitions.create("margin-left"),

    [theme.breakpoints.up(theme.breakpoints.values.xl - 1)]: {
      marginLeft: theme.sidebar.defaultWidth,
      maxWidth: `calc(100% - ${theme.sidebar.defaultWidth}px)`
    },

    "& > *": {
      overflowY: "auto"
    }
  }
});

export default withStyles(styles as any)(SiteLayout);
